import React, { useState, useEffect, useRef, useCallback } from 'react';
import LoadingOverlay from './LoadingOverlay';
import BASE_URL from '../utils/api';
import '../sidebar/Home.css';
import { CATEGORIAS_EMOJI, ESTILO_BANDEIRA } from '../utils/emojis';

// ─── Auth ─────────────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('authToken');

const decodeJwt = (token) => {
  try {
    const p = token.split('.');
    if (p.length < 2) return null;
    return JSON.parse(atob(p[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return null; }
};

const checkIsCeo = () => {
  const token = getToken();
  if (!token) return false;
  const payload = decodeJwt(token);
  return String(payload?.['TipoUsuarioLoginId'] || '') === '5' || String(payload?.['tipoUsuarioLoginId'] || '') === '5';
};

const getLoginFromToken = () => {
  const token = getToken();
  if (!token) return '';
  const payload = decodeJwt(token);
  return payload?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || payload?.unique_name || payload?.name || payload?.sub || '';
};

// ─── API ───────────────────────────────────────────────────────────────────────
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const url = `${BASE_URL}/${endpoint}`;
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const config = { method, headers };
  if (data && method !== 'GET') config.body = JSON.stringify(data);
  const res = await fetch(url, config);
  if (res.status === 204) return null;
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error(json?.mensagem || json?.message || `Erro ${res.status}`);
  return json;
};

const apiUpload = async (endpoint, formData) => {
  const url = `${BASE_URL}/${endpoint}`;
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { method: 'POST', headers, body: formData });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error(json?.mensagem || json?.message || `Erro ${res.status}`);
  return json;
};

const fetchBlobUrl = async (arquivoId) => {
  try {
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE_URL}/ForumPostagemArquivo/${arquivoId}`, { headers });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch { return null; }
};

// ─── Formatação ────────────────────────────────────────────────────────────────
const fmtDate = (dt) => {
  try {
    const d = new Date(dt);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

// ─── Emoji: mesma lista e ordem do chat e do publicar postagem (utils/emojis) ───

// ─── AuthImg ───────────────────────────────────────────────────────────────────
function AuthImg({ arquivoId, nome, size = 140 }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    setLoading(true);
    fetchBlobUrl(arquivoId).then(url => {
      if (mounted.current) { setBlobUrl(url); setLoading(false); }
    });
    return () => {
      mounted.current = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line
  }, [arquivoId]);

  return (
    <div
      onClick={() => blobUrl && window.open(blobUrl, '_blank')}
      style={{
        width: size, height: size, borderRadius: 6, overflow: 'hidden',
        background: '#f0f0f0', display: 'flex', alignItems: 'center',
        justifyContent: 'center', cursor: blobUrl ? 'pointer' : 'default',
        border: '1px solid #e8e8e8', flexShrink: 0,
      }}
      title={nome}
    >
      {loading && <span style={{ fontSize: 11, color: '#bbb' }}>⏳</span>}
      {blobUrl && <img src={blobUrl} alt={nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
      {!loading && !blobUrl && <span style={{ fontSize: 20 }}>🖼️</span>}
    </div>
  );
}

// ─── CommentsSection ───────────────────────────────────────────────────────────
function CommentsSection({ postagemId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [text, setText] = useState('');
  const loginAtual = getLoginFromToken();

  useEffect(() => {
    apiCall(`ForumPostagemComentario/postagem/${postagemId}?skip=0&take=50`)
      .then(res => setComments(res?.Items || res?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [postagemId]);

  const send = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const c = await apiCall('ForumPostagemComentario', 'POST', { ForumPostagemId: postagemId, Texto: text.trim() });
      if (c) setComments(prev => [{ ...c, Login: c.Login || loginAtual }, ...prev]);
      setText('');
    } catch (e) { alert('Erro ao comentar: ' + e.message); }
    finally { setPosting(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Remover comentário?')) return;
    try {
      await apiCall(`ForumPostagemComentario/${id}`, 'DELETE');
      setComments(prev => prev.filter(c => (c.Id || c.id) !== id));
    } catch (e) { alert('Erro: ' + e.message); }
  };

  return (
    <div className="comments-section">
      {loading && <div style={{ color: '#bbb', fontSize: 13 }}>Carregando...</div>}
      {!loading && comments.length === 0 && (
        <div style={{ color: '#bbb', fontSize: 13, marginBottom: 8 }}>Nenhum comentário ainda.</div>
      )}
      <div className="comment-input-row">
        <input
          className="comment-input"
          type="text"
          placeholder="Escreva um comentário..."
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <button type="button" className="comment-button" onClick={send} disabled={posting || !text.trim()}>Comentar</button>
      </div>
      {comments.length > 0 && (
        <div className="comment-list">
          {comments.map(c => {
            const id = c.Id || c.id;
            const login = c.Login || c.login || '';
            const texto = c.Texto || c.texto || '';
            const data = c.DataHora || c.dataHora || '';
            return (
              <div key={id} className="comment-item">
                <div className="comment-header">
                  <strong>{login}</strong>
                  <span className="comment-time">{fmtDate(data)}</span>
                  {login === loginAtual && (
                    <button
                      onClick={() => del(id)}
                      title="Remover comentário"
                      aria-label="Remover comentário"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 13, marginLeft: 6 }}
                      onMouseEnter={e => e.currentTarget.style.color = '#d32f2f'}
                      onMouseLeave={e => e.currentTarget.style.color = '#ccc'}
                    >×</button>
                  )}
                </div>
                <div className="comment-text">{texto}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── PostCard ──────────────────────────────────────────────────────────────────
function PostCard({ post, isCeo, loginAtual, onDeleted }) {
  const id = post.id || post.Id;
  const texto = post.Texto || post.texto || '';
  const login = post.Login || post.login || '';
  const empresa = post.NomeFantasia || post.nomeFantasia || post.RazaoSocial || post.razaoSocial || '';
  const data = post.DataHora || post.dataHora || '';

  const [arquivos, setArquivos] = useState([]);
  const [likesCount, setLikesCount] = useState(0);
  const [curtiu, setCurtiu] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [commentOpen, setCommentOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const MAX = 280;

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      apiCall(`ForumPostagemArquivo/postagem/${id}`),
      apiCall(`ForumPostagemCurtida/postagem/${id}`),
      apiCall(`ForumPostagemCurtida/postagem/${id}/minha`),
      apiCall(`ForumPostagemComentario/postagem/${id}?skip=0&take=1`),
    ]).then(([arq, cur, minha, com]) => {
      if (!alive) return;
      if (arq.status === 'fulfilled' && Array.isArray(arq.value))
        setArquivos(arq.value.map(a => ({ id: a.Id || a.id, tipo: a.TipoArquivo || a.tipoArquivo || '', nome: a.NomeArquivo || a.nomeArquivo || '' })));
      if (cur.status === 'fulfilled') setLikesCount(cur.value?.Total ?? cur.value?.total ?? 0);
      if (minha.status === 'fulfilled') setCurtiu(minha.value?.Curtiu ?? minha.value?.curtiu ?? false);
      if (com.status === 'fulfilled') setCommentCount(com.value?.Total ?? com.value?.total ?? 0);
    });
    return () => { alive = false; };
  }, [id]);

  const handleLike = async () => {
    const prev = curtiu;
    setCurtiu(!prev);
    setLikesCount(c => prev ? Math.max(0, c - 1) : c + 1);
    try { await apiCall('ForumPostagemCurtida', 'POST', { ForumPostagemId: id }); }
    catch { setCurtiu(prev); setLikesCount(c => prev ? c + 1 : Math.max(0, c - 1)); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir esta postagem?')) return;
    setDeleting(true);
    try { await apiCall(`ForumPostagem/${id}`, 'DELETE'); onDeleted(id); }
    catch (e) { alert('Erro: ' + e.message); setDeleting(false); }
  };

  const images = arquivos.filter(a => a.tipo?.startsWith('image/'));
  const others = arquivos.filter(a => !a.tipo?.startsWith('image/'));

  return (
    <div className="post-card" style={{ position: 'relative' }}>
      {isCeo && login === loginAtual && (
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
          <button type="button" title="Excluir postagem" aria-label="Excluir postagem" onClick={handleDelete} disabled={deleting} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 1 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#e53935" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
          </button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <div className="post-author" style={{ fontWeight: 'bold', color: '#333' }}>{login || 'Usuário'}</div>
        <div className="post-time">{fmtDate(data)}</div>
      </div>
      {empresa && <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{empresa}</div>}

      <div className="post-description">
        {texto.length > MAX && !expanded ? texto.slice(0, MAX) + '…' : texto}
        {texto.length > MAX && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{ background: 'none', border: 'none', color: 'rgb(235,133,0)', cursor: 'pointer', fontWeight: 700, fontSize: 13, padding: '0 4px' }}
          >
            {expanded ? 'Ver menos' : 'Ver mais'}
          </button>
        )}
      </div>

      {images.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: images.length === 1 ? '1fr' : images.length === 2 ? '1fr 1fr' : 'repeat(3,1fr)',
          gap: 4, marginBottom: 10, borderRadius: 8, overflow: 'hidden',
        }}>
          {images.slice(0, 6).map(a => (
            <AuthImg key={a.id} arquivoId={a.id} nome={a.nome} size={images.length === 1 ? 280 : 110} />
          ))}
        </div>
      )}

      {others.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {others.map(a => (
            <span
              key={a.id}
              onClick={() => fetchBlobUrl(a.id).then(url => url && window.open(url, '_blank'))}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                background: '#f5f5f5', borderRadius: 6, padding: '6px 10px',
                border: '1px solid #e8e8e8', fontSize: 13,
              }}
            >
              📄 {a.nome || `Arquivo ${a.id}`}
            </span>
          ))}
        </div>
      )}

      <div className="post-separator" />
      <div className="post-stats">
        <span>{likesCount} curtidas</span>
        <span className="stats-sep">•</span>
        <span
          role="button"
          tabIndex={0}
          aria-label="Abrir comentários"
          className="post-stats-comments"
          onClick={() => setCommentOpen(o => !o)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCommentOpen(o => !o); } }}
        >
          {commentCount} comentários
        </span>
      </div>
      <div className="post-actions">
        <button type="button" className={`action-btn like-btn ${curtiu ? 'liked' : ''}`} onClick={handleLike} aria-pressed={curtiu}>
          <svg className="icon heart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <span>Curtir</span>
        </button>
        <button type="button" className={`action-btn comments-btn ${commentOpen ? 'open' : ''}`} onClick={() => setCommentOpen(o => !o)}>
          <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <span>Comentários</span>
        </button>
      </div>

      {commentOpen && <CommentsSection postagemId={id} />}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────
export default function ForumPostagem() {
  const isCeo = checkIsCeo();
  const loginAtual = getLoginFromToken();

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const TAKE = 10;

  const [expanded, setExpanded] = useState(false);
  const [texto, setTexto] = useState('');
  const [textoError, setTextoError] = useState('');
  const [postError, setPostError] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiCat, setEmojiCat] = useState('faces');
  const emojiRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const h = (ev) => {
      if (!emojiOpen) return;
      if (emojiRef.current && !emojiRef.current.contains(ev.target)) setEmojiOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [emojiOpen]);

  const loadPosts = useCallback(async (s = 0, append = false) => {
    setLoadingFeed(true);
    try {
      const res = await apiCall(`ForumPostagem?skip=${s}&take=${TAKE}`);
      setTotal(res?.Total ?? res?.total ?? 0);
      const items = res?.Items || res?.items || [];
      setPosts(prev => append ? [...prev, ...items] : items);
      setSkip(s + items.length);
    } catch (e) { alert('Erro ao carregar: ' + e.message); }
    finally { setLoadingFeed(false); }
  }, []);

  useEffect(() => { loadPosts(0, false); }, [loadPosts]);

  const handleImagesSelected = async (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type?.startsWith('image/'));
    e.target.value = '';
    const adds = await Promise.all(files.map(async f => ({ file: f, preview: URL.createObjectURL(f) })));
    setUploadFiles(prev => [...prev, ...adds]);
    setExpanded(true);
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setUploadFiles(prev => [...prev, ...files.map(f => ({ file: f, preview: null }))]);
    setExpanded(true);
  };

  const removeFile = (key) => {
    setUploadFiles(prev => {
      prev.forEach(p => { if (`${p.file.name}_${p.file.size}` === key && p.preview) URL.revokeObjectURL(p.preview); });
      return prev.filter(p => `${p.file.name}_${p.file.size}` !== key);
    });
  };

  const handlePost = async () => {
    // Lê de estado E do DOM para garantir que pega o valor real
    const textoState = texto.trim();
    const textoDOM = (textareaRef.current?.value || '').trim();
    const textoFinal = textoState || textoDOM;
    console.log('[ForumPostagem] handlePost chamado. state.length=', textoState.length, 'dom.length=', textoDOM.length);
    if (!textoFinal) {
      console.warn('[ForumPostagem] texto vazio, mostrando erro.');
      setTextoError('Escreva algo antes de postar.');
      return;
    }
    setPosting(true);
    setPostError('');
    try {
      console.log('[ForumPostagem] Enviando POST /ForumPostagem com texto=', textoFinal.slice(0,40));
      const created = await apiCall('ForumPostagem', 'POST', { Texto: textoFinal });
      console.log('[ForumPostagem] Resposta:', created);
      const newId = created?.id || created?.Id;
      if (newId && uploadFiles.length > 0) {
        for (const uf of uploadFiles) {
          try {
            const fd = new FormData();
            fd.append('File', uf.file, uf.file.name);
            fd.append('ForumPostagemId', newId);
            fd.append('TipoArquivo', uf.file.type || 'application/octet-stream');
            fd.append('NomeArquivo', uf.file.name);
            await apiUpload('ForumPostagemArquivo', fd);
          } catch (uploadErr) {
            console.warn('[ForumPostagem] Erro no upload de arquivo:', uploadErr);
          }
        }
      }
      uploadFiles.forEach(uf => uf.preview && URL.revokeObjectURL(uf.preview));
      setUploadFiles([]); setTexto(''); setTextoError(''); setPostError(''); setExpanded(false);
      if (textareaRef.current) textareaRef.current.value = '';
      await loadPosts(0, false);
    } catch (e) {
      console.error('[ForumPostagem] Erro ao publicar:', e);
      setPostError('Erro ao publicar: ' + (e.message || 'Erro desconhecido. Verifique o console.'));
    }
    finally { setPosting(false); }
  };

  const handleCancel = () => {
    const hasContent = texto.trim() || uploadFiles.length > 0;
    if (hasContent && !window.confirm('Descartar publicação?')) return;
    uploadFiles.forEach(uf => uf.preview && URL.revokeObjectURL(uf.preview));
    setUploadFiles([]); setTexto(''); setTextoError(''); setExpanded(false); setEmojiOpen(false);
  };

  const images = uploadFiles.filter(uf => uf.file?.type?.startsWith('image/'));
  const otherFiles = uploadFiles.filter(uf => !uf.file?.type?.startsWith('image/'));

  return (
    <div style={{
      minHeight: 'calc(100vh - var(--global-header-height, 60px))',
      background: '#f0f0f0',
      padding: '20px',
      boxSizing: 'border-box',
    }}>
      <LoadingOverlay isLoading={posting} message="Publicando..." />

      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* Header banner */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 18,
          marginBottom: 20,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, flexShrink: 0,
            background: 'rgb(235, 133, 0)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32,
            boxShadow: '0 2px 8px rgba(235,133,0,0.25)',
          }}>
            📢
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 22, color: '#111', lineHeight: 1.2, marginBottom: 4 }}>
              Forum Empresas
            </div>
            <div style={{ fontSize: 14, color: '#666', lineHeight: 1.5 }}>
              Aqui você vai poder ver as principais dúvidas e sugestões de outras empresas, além de atualizações e avisos.
            </div>
          </div>
        </div>

        {/* Composer (CEO only) */}
        {isCeo && (
          <div className="feed-card" style={{ marginBottom: 16 }}>
            <div className="composer">
              {!expanded && (
                <div className="input-row">
                  <input
                    className="short-input"
                    placeholder="O que você está pensando?"
                    value={texto}
                    onChange={e => { setTexto(e.target.value); if (textoError && e.target.value.trim()) setTextoError(''); }}
                    onFocus={() => setExpanded(true)}
                  />
                </div>
              )}

              {expanded && (
                <div className="composer-expanded">
                  {images.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: images.length === 1 ? '1fr' : 'repeat(3,1fr)',
                      gap: 6, marginBottom: 10, borderRadius: 10, overflow: 'hidden',
                    }}>
                      {images.map(uf => {
                        const key = `${uf.file.name}_${uf.file.size}`;
                        return (
                          <div key={key} style={{ position: 'relative' }}>
                            <img src={uf.preview} alt={uf.file.name} style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block', borderRadius: 8 }} />
                            <button type="button" onClick={() => removeFile(key)} style={{
                              position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)',
                              color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, fontSize: 14,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>×</button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {otherFiles.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                      {otherFiles.map(uf => {
                        const key = `${uf.file.name}_${uf.file.size}`;
                        return (
                          <div key={key} style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: '#f5f5f5', borderRadius: 6, padding: '6px 10px', border: '1px solid #e8e8e8', fontSize: 13,
                          }}>
                            <span>📄</span>
                            <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{uf.file.name}</span>
                            <button type="button" onClick={() => removeFile(key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 16 }}>×</button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Público badge — idêntico ao Home */}
                  <div className="groups-selected-top" style={{ marginBottom: 8 }}>
                    <span className="groups-public-inline" style={{ fontWeight: 700, marginRight: 8 }}>Público</span>
                  </div>

                  {textoError && (
                    <div style={{ color: '#d32f2f', fontSize: 13, marginBottom: 6, background: 'rgba(211,47,47,0.06)', padding: 8, borderRadius: 6 }}>
                      {textoError}
                    </div>
                  )}

                  <textarea
                    ref={textareaRef}
                    placeholder="Escreva aqui sua publicação, detalhes, links..."
                    value={texto}
                    onChange={e => { setTexto(e.target.value); if (textoError && e.target.value.trim()) setTextoError(''); }}
                    autoFocus
                  />

                  <div ref={emojiRef} style={{ position: 'relative' }}>
                    {postError && (
                      <div style={{
                        color: '#d32f2f', fontSize: 13, marginBottom: 8,
                        background: 'rgba(211,47,47,0.07)', padding: '8px 12px',
                        borderRadius: 6, border: '1px solid rgba(211,47,47,0.2)',
                      }}>
                        ⚠️ {postError}
                      </div>
                    )}
                    <div className="icon-row">
                      <button className="icon-btn" title="Adicionar foto" onClick={() => imageInputRef.current?.click()}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                          <path d="M21 19V7a2 2 0 0 0-2-2h-3.586l-.707-.707A.999.999 0 0 0 13.586 3H10.414a1 1 0 0 0-.707.293L9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
                        </svg>
                      </button>
                      <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImagesSelected} />

                      <button className="icon-btn clip" title="Anexar arquivo" onClick={() => fileInputRef.current?.click()}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M21.44 11.05l-8.482 8.487a5.5 5.5 0 1 1-7.778-7.778L14.4 4.026a3.5 3.5 0 0 1 4.95 4.95l-9.192 9.198a1.5 1.5 0 0 1-2.122-2.122l8.484-8.489" strokeLinecap="round"/>
                        </svg>
                      </button>
                      <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.xls,.xlsx" style={{ display: 'none' }} onChange={handleFilesSelected} />

                      <span style={{ position: 'relative' }}>
                        <button className="icon-btn emoji-container" title="Emoji" onClick={(e) => { e.stopPropagation(); setEmojiOpen(o => !o); }}>
                          <svg viewBox="0 0 24 24" width="18" height="18">
                            <circle cx="12" cy="12" r="10" fill="currentColor"/>
                            <circle cx="9" cy="10" r="0.9" fill="#fff"/>
                            <circle cx="15" cy="10" r="0.9" fill="#fff"/>
                            <path d="M12 17.5c2.33 0 4.31-1.46 5.11-3.5h-10.22c.8 2.04 2.78 3.5 5.11 3.5z" fill="#fff"/>
                          </svg>
                        </button>
                        {emojiOpen && (
                          <div className="emoji-dropdown" role="menu">
                            <div className="emoji-tabs">
                              {CATEGORIAS_EMOJI.map(cat => (
                                <button key={cat.chave} className={`emoji-tab ${emojiCat === cat.chave ? 'active' : ''}`} onClick={() => setEmojiCat(cat.chave)} title={cat.titulo} style={cat.bandeiras ? ESTILO_BANDEIRA : undefined}>
                                  {cat.emojis[0]}
                                </button>
                              ))}
                            </div>
                            <div className="emoji-grid">
                              {(CATEGORIAS_EMOJI.find(cat => cat.chave === emojiCat) || CATEGORIAS_EMOJI[0]).emojis.map((em, i) => (
                                <button key={i} className="emoji-item" style={emojiCat === 'flags' ? ESTILO_BANDEIRA : undefined} onClick={() => { setTexto(t => t + em); setEmojiOpen(false); }}>{em}</button>
                              ))}
                            </div>
                          </div>
                        )}
                      </span>

                      <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center' }}>
                        <button onClick={handleCancel} className="button-create" style={{ background: '#9e9e9e', marginLeft: 0 }}>Cancelar</button>
                        <button onClick={handlePost} className="button-create" style={{ marginLeft: 0 }} disabled={posting}>
                          {posting ? 'Enviando...' : 'Postar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Título */}
        <div style={{ textAlign: 'center', marginTop: 4, marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>
            Publicações do Fórum {loadingFeed ? '(Carregando...)' : `(${posts.length})`}
          </h3>
          {!loadingFeed && posts.length === 0 && (
            <div style={{ color: '#666', marginTop: 8 }}>Nenhuma publicação encontrada.</div>
          )}
        </div>

        {/* Posts */}
        <div className="post-list" style={{ overflow: 'visible' }}>
          {posts.map(p => (
            <PostCard
              key={p.id || p.Id}
              post={p}
              isCeo={isCeo}
              loginAtual={loginAtual}
              onDeleted={(id) => { setPosts(prev => prev.filter(x => (x.id || x.Id) !== id)); setTotal(t => Math.max(0, t - 1)); }}
            />
          ))}
        </div>

        {posts.length < total && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button
              onClick={() => loadPosts(skip, true)}
              disabled={loadingFeed}
              style={{
                background: 'none', border: '1px solid #ddd', borderRadius: 20,
                padding: '10px 28px', cursor: 'pointer', color: '#666', fontSize: 14, fontWeight: 600,
              }}
            >
              {loadingFeed ? 'Carregando…' : `Ver mais (${total - posts.length} restantes)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
